import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  DataGrid,
  Text,
  useGlobalization,
  GridRenderCellParams,
  error,
  useUnits,
} from '@innovyze/stylovyze';
import { getLengthCostPerDisplayLabel } from '@Utils/lengthUnitCost';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import { CostTableListData } from '@Types/costTable.types';
import { Link } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useAllAssetTypes, useSystemTypes } from '@Hooks/assetSchemaHooks';
import { addRouteNamespace } from '@Utils/actions';
import { checkCostTableInUse } from '@Services/costTables.services';
import DeleteCostTableDialog from '@Components/DeleteCostTableDialog';
import {
  selectGroupSets,
  selectGroupSetsIsLoading,
} from '@Selectors/groupSets.selector';
import { useCompanyDateTime } from '@innovyze/stylovyze';
import { formatDateTimeWithFormatDate } from '@innovyze/lib-am-common/Utils';
import { CostTableListToolbar } from './CostTableListToolbar';
import { useCostTableListGridState } from '@Hooks/useCostTableSelectors';
import { saveCostTableListGridState } from '@Actions/costTables.actions';
import { useHasEditAccess } from '@Hooks/useHasEditAccess';

interface CostTableListProps {
  costTableListData: {
    costTables: CostTableListData[];
    isLoading: boolean;
    isWaiting: boolean;
    status: string | null;
    pagination: {
      page: number;
      slice: number;
      total: number;
      pages: number;
    };
  };
  setCreateDialogOpen: (open: boolean) => void;
  onRefresh: () => void;
  handleTableCopy: (id: string) => Promise<void>;
  handleTableDelete: (id: string, name: string) => Promise<void>;
}

interface OptionsColumnProps extends GridRenderCellParams {
  onTableCopy: (id: string) => Promise<void>;
  onTableDelete: (id: string, name: string) => void;
  isCopying: boolean;
}

const OptionsColumn: React.FC<OptionsColumnProps> = ({
  row,
  onTableCopy,
  onTableDelete,
  isCopying,
}) => {
  const { t } = useGlobalization();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleTableCopy = async () => {
    await onTableCopy(row.id);
    handleClose();
  };

  const handleTableDelete = () => {
    onTableDelete(row.id, row.name);
    handleClose();
  };

  return (
    <>
      <IconButton
        aria-label="more"
        id={`options-button-${row.id}`}
        aria-controls={open ? `options-menu-${row.id}` : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        size="small">
        <MoreVert />
      </IconButton>
      <Menu
        id={`options-menu-${row.id}`}
        MenuListProps={{ 'aria-labelledby': `options-button-${row.id}` }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}>
        <MenuItem onClick={handleTableCopy} disabled={isCopying}>
          {t('Copy Cost Table')}
        </MenuItem>
        <MenuItem onClick={handleTableDelete}>
          {t('Delete Cost Table')}
        </MenuItem>
      </Menu>
    </>
  );
};

const CostTablesListGrid: React.FC<CostTableListProps> = ({
  costTableListData,
  setCreateDialogOpen,
  onRefresh,
  handleTableCopy,
  handleTableDelete,
}) => {
  const { t } = useGlobalization();
  const { system } = useUnits();
  const { formatDate } = useCompanyDateTime();
  const history = useHistory();
  const dispatch = useDispatch();
  const hasEditAccess = useHasEditAccess();
  const savedGridState = useCostTableListGridState();
  // selectGroupSets / selectGroupSetsIsLoading wrap useSelector internally — they are
  // hooks despite the select* naming convention used throughout this codebase.
  const groupSets = selectGroupSets();
  const groupSetsIsLoading = selectGroupSetsIsLoading();
  const isLoading =
    costTableListData.isLoading ||
    costTableListData.isWaiting ||
    groupSetsIsLoading;

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCostTable, setSelectedCostTable] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCheckingInUse, setIsCheckingInUse] = useState(false);
  const [isInUse, setIsInUse] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [searchValue, setSearchValue] = useState(
    () => savedGridState?.searchValue ?? ''
  );
  const [paginationModel, setPaginationModel] = useState(
    () => savedGridState?.paginationModel ?? { page: 0, pageSize: 25 }
  );
  const [sortModel, setSortModel] = useState(
    () =>
      savedGridState?.sortModel ?? [
        { field: 'lastUpdated', sort: 'desc' as const },
      ]
  );
  const [lastKnownTotal, setLastKnownTotal] = useState(0);

  const saveState = useCallback(
    (
      updates: Partial<{
        searchValue: string;
        paginationModel: { page: number; pageSize: number };
        sortModel: { field: string; sort: 'asc' | 'desc' }[];
      }>
    ) => {
      dispatch(
        saveCostTableListGridState({
          searchValue,
          paginationModel,
          sortModel,
          ...updates,
        })
      );
    },
    [dispatch, searchValue, paginationModel, sortModel]
  );

  const findGroupSet = useCallback(
    (id: string) => groupSets.find(gs => gs._id === id),
    [groupSets]
  );

  const assetTypes = useMemo(
    () =>
      Array.from(
        new Set(
          costTableListData.costTables
            .map(row => findGroupSet(row.assetGroupSet)?.assetType)
            .filter((t): t is string => Boolean(t))
        )
      ),
    [costTableListData.costTables, findGroupSet]
  );

  const allAssetTypes = useAllAssetTypes();
  const systemTypes = useSystemTypes();

  // Display names for the asset types in use. While the catalog loads or if it
  // fails, entries are absent and callers fall back to the raw group set values.
  const assetSchemaByType = useMemo(() => {
    const systemTypeNames = new Map(
      (systemTypes.data ?? []).map(st => [st.id, t(st.displayName)])
    );
    const assetTypeById = new Map(
      (allAssetTypes.data ?? []).map(at => [at.id, at])
    );
    return Object.fromEntries(
      assetTypes.flatMap(id => {
        const summary = assetTypeById.get(id);
        const systemTypeName = summary
          ? systemTypeNames.get(summary.systemTypeId)
          : undefined;
        return summary && systemTypeName
          ? [[id, { displayName: t(summary.displayName), systemTypeName }]]
          : [];
      })
    ) as Record<string, { displayName: string; systemTypeName: string }>;
  }, [assetTypes, allAssetTypes.data, systemTypes.data, t]);

  const filteredRows = searchValue.trim()
    ? costTableListData.costTables.filter(row => {
        const search = searchValue.toLowerCase();
        const gs = findGroupSet(row.assetGroupSet);
        const schema = gs?.assetType
          ? assetSchemaByType[gs.assetType]
          : undefined;
        return (
          row.name.toLowerCase().includes(search) ||
          (gs?.name ?? '').toLowerCase().includes(search) ||
          // Search raw group set values as fallback while schemas load
          (gs?.systemType ?? '').toLowerCase().includes(search) ||
          (gs?.assetType ?? '').toLowerCase().includes(search) ||
          // Search schema display names once loaded (e.g. "Pipe" matches "wwPipe")
          (schema?.systemTypeName ?? '').toLowerCase().includes(search) ||
          (schema?.displayName ?? '').toLowerCase().includes(search)
        );
      })
    : costTableListData.costTables;

  useEffect(() => {
    if (!isLoading) {
      setLastKnownTotal(filteredRows.length);
    }
  }, [isLoading, filteredRows.length]);

  const handleDeleteDialogOpen = async (id: string, name: string) => {
    setSelectedCostTable({ id, name });
    setIsDeleteDialogOpen(true);
    setIsInUse(false);
    setIsCheckingInUse(true);
    try {
      const inUse = await checkCostTableInUse(id);
      setIsInUse(inUse);
    } catch (err) {
      // API failure is non-fatal: dialog stays open so user can still proceed
      // (fail-open). isInUse remains false. Log and surface a toast so the
      // user knows the check was skipped and should exercise their own judgement.
      console.error(
        '[CostTablesListGrid] Failed to check if cost table is in use:',
        err
      );
      dispatch(
        error(t('Could not verify cost table usage — proceed with caution'))
      );
    } finally {
      setIsCheckingInUse(false);
    }
  };

  const handleDeleteDialogClose = () => {
    setIsDeleteDialogOpen(false);
    setSelectedCostTable(null);
    setIsCheckingInUse(false);
    setIsInUse(false);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCostTable) return;
    setIsDeleting(true);
    try {
      await handleTableDelete(selectedCostTable.id, selectedCostTable.name);
      handleDeleteDialogClose();
    } catch (error) {
      console.error('Error deleting cost table:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCopyWithLoading = async (id: string) => {
    setIsCopying(true);
    try {
      await handleTableCopy(id);
    } catch (error) {
      console.error('Error copying cost table:', error);
    } finally {
      setIsCopying(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    saveState({
      searchValue: value,
      paginationModel: { ...paginationModel, page: 0 },
    });
  };

  const displayTotal = isLoading ? lastKnownTotal : filteredRows.length;

  return (
    <>
      <DeleteCostTableDialog
        open={isDeleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onConfirm={handleConfirmDelete}
        costTableName={selectedCostTable?.name || ''}
        isLoading={isDeleting}
        isCheckingInUse={isCheckingInUse}
        isInUse={isInUse}
      />
      <CostTableListToolbar
        onRefresh={onRefresh}
        searchValue={searchValue}
        onSearch={handleSearch}
        onCreateClick={() => setCreateDialogOpen(true)}
        loading={isLoading}
        hasEditAccess={hasEditAccess}
      />
      <DataGrid
        loading={isLoading}
        skeletonRowCount={paginationModel.pageSize}
        columns={[
          {
            field: 'name',
            headerName: t('Cost Table Name'),
            renderHeader: () => (
              <Text style={{ fontWeight: 'bold' }}>{t('Cost Table Name')}</Text>
            ),
            flex: 1,
            type: 'string',
            filterable: false,
            align: 'left',
            headerAlign: 'left',
            renderCell: params => (
              <Link
                underline="hover"
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  cursor: 'pointer',
                }}
                title={params.row.name}
                onClick={() => {
                  history.push(addRouteNamespace(`${params.row.id}`));
                }}>
                {params.row.name}
              </Link>
            ),
          },
          {
            field: 'systemType',
            headerName: t('System Type'),
            renderHeader: () => (
              <Text style={{ fontWeight: 'bold' }}>{t('System Type')}</Text>
            ),
            flex: 1,
            type: 'string',
            filterable: false,
            valueGetter: params => {
              const gs = findGroupSet(params.row.assetGroupSet);
              return gs?.assetType
                ? assetSchemaByType[gs.assetType]?.systemTypeName ??
                    gs.systemType
                : gs?.systemType ?? '';
            },
            align: 'left',
            headerAlign: 'left',
            renderCell: params => {
              const gs = findGroupSet(params.row.assetGroupSet);
              const label = gs?.assetType
                ? assetSchemaByType[gs.assetType]?.systemTypeName ??
                  gs.systemType
                : gs?.systemType ?? '';
              return <Text>{label}</Text>;
            },
          },
          {
            field: 'assetType',
            headerName: t('Asset Type'),
            renderHeader: () => (
              <Text style={{ fontWeight: 'bold' }}>{t('Asset Type')}</Text>
            ),
            flex: 1,
            type: 'string',
            filterable: false,
            valueGetter: params => {
              const gs = findGroupSet(params.row.assetGroupSet);
              return gs?.assetType
                ? assetSchemaByType[gs.assetType]?.displayName ?? gs.assetType
                : gs?.assetType ?? '';
            },
            align: 'left',
            headerAlign: 'left',
            renderCell: params => {
              const gs = findGroupSet(params.row.assetGroupSet);
              const label = gs?.assetType
                ? assetSchemaByType[gs.assetType]?.displayName ?? gs.assetType
                : gs?.assetType ?? '';
              return <Text>{label}</Text>;
            },
          },
          {
            field: 'assetGroupSet',
            headerName: t('Asset Group Set'),
            renderHeader: () => (
              <Text style={{ fontWeight: 'bold' }}>{t('Asset Group Set')}</Text>
            ),
            flex: 1,
            type: 'string',
            filterable: false,
            align: 'left',
            headerAlign: 'left',
            valueGetter: params => {
              const gs = groupSets.find(
                g => g._id === params.row.assetGroupSet
              );
              return gs?.name ?? params.row.assetGroupSet;
            },
            renderCell: params => {
              const gs = findGroupSet(params.row.assetGroupSet);
              return <Text>{gs?.name ?? params.row.assetGroupSet}</Text>;
            },
          },
          {
            field: 'costPer',
            headerName: t('Cost per'),
            renderHeader: () => (
              <Text style={{ fontWeight: 'bold' }}>{t('Cost per')}</Text>
            ),
            flex: 1,
            type: 'string',
            filterable: false,
            align: 'left',
            headerAlign: 'left',
            renderCell: params => {
              const value = params.row.costPer;
              return (
                <Text>
                  {value === 'length'
                    ? getLengthCostPerDisplayLabel(t, system)
                    : value}
                </Text>
              );
            },
          },
          {
            field: 'lastUpdated',
            headerName: t('Last Updated'),
            renderHeader: () => (
              <Text style={{ fontWeight: 'bold' }}>{t('Last Updated')}</Text>
            ),
            flex: 1,
            type: 'string',
            filterable: false,
            align: 'left',
            headerAlign: 'left',
            renderCell: params => {
              const formatted = formatDateTimeWithFormatDate(
                params.row.lastUpdated,
                formatDate
              );
              return <Text>{formatted ?? ''}</Text>;
            },
          },
          ...(hasEditAccess
            ? [
                {
                  field: 'actions',
                  headerName: '',
                  width: 60,
                  sortable: false,
                  filterable: false,
                  align: 'center' as const,
                  headerAlign: 'center' as const,
                  renderCell: (params: GridRenderCellParams) => (
                    <OptionsColumn
                      {...params}
                      onTableCopy={handleCopyWithLoading}
                      onTableDelete={handleDeleteDialogOpen}
                      isCopying={isCopying}
                    />
                  ),
                },
              ]
            : []),
        ]}
        disableColumnMenu
        disableColumnPinning
        disableColumnReorder
        sortingOrder={['desc', 'asc']}
        sortModel={sortModel}
        onSortModelChange={model => {
          const normalized = model.filter(
            (m): m is { field: string; sort: 'asc' | 'desc' } =>
              m.sort === 'asc' || m.sort === 'desc'
          );
          setSortModel(normalized);
          saveState({ sortModel: normalized });
        }}
        footerHelper={t('{{from}}-{{to}} of {{total}}', {
          from:
            displayTotal === 0
              ? 0
              : paginationModel.page * paginationModel.pageSize + 1,
          to: Math.min(
            (paginationModel.page + 1) * paginationModel.pageSize,
            displayTotal
          ),
          total: displayTotal,
        })}
        rows={filteredRows}
        pagination
        pageSizeOptions={[5, 10, 15, 25, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={model => {
          setPaginationModel(model);
          saveState({ paginationModel: model });
        }}
      />
    </>
  );
};

export default CostTablesListGrid;
